import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Avatar,
  useTheme,
  Tooltip,
  IconButton,
  Skeleton
} from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ProductDrillDownModal from '../../Components/ProductDrillDownModal';
import { useProductDrillDown } from '../../Hooks/useProductDrillDown';

import { IcmsPorProduto } from './types';

type TabelaIcmsProdutosProps = {
  data: IcmsPorProduto[];
  loading?: boolean;
  title: string;
  spedFiscalId?: number | null;
};

const formatCurrency = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

const TableIcmsProdutos: React.FC<TabelaIcmsProdutosProps> = ({ 
  data, 
  loading = false, 
  title, 
  spedFiscalId 
}) => {
  const theme = useTheme();
  const {
    isModalOpen,
    drillDownData,
    loading: drillDownLoading,
    selectedProductName,
    openModal,
    closeModal
  } = useProductDrillDown();

  const handleProductClick = async (item: IcmsPorProduto) => {
    if (!spedFiscalId) return;
    await openModal(item.descr_compl || 'Produto', item.cod_item, spedFiscalId);
  };

  return (
    <>
      <Card sx={{ p: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <Avatar
              sx={{
                bgcolor: theme.palette.error.light,
                color: theme.palette.error.main,
                width: 36,
                height: 36,
                mr: 1,
              }}
            >
              <BalanceIcon />
            </Avatar>
            <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
              {title}
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    '#',
                    'Código',
                    'Descrição Completa',
                    'Crédito ICMS',
                    'Débito ICMS',
                    'Saldo ICMS',
                    ...(spedFiscalId ? ['Ações'] : [])
                  ].map((header) => (
                    <TableCell 
                      key={header} 
                      align={header.includes('Descrição') || header === 'Código' ? 'left' : header === '#' ? 'center' : 'right'}
                      sx={header === '#' ? { width: '50px' } : {}}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, textTransform: header === '#' ? 'uppercase' : 'capitalize', color: theme.palette.text.secondary }}>
                        {header}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow
                      key={`skeleton-${index}`}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5'
                      }}
                    >
                      <TableCell align="center" sx={{ width: '50px' }}>
                        <Skeleton variant="text" width="100%" height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="80%" height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="90%" height={16} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton variant="text" width="70%" height={16} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton variant="text" width="70%" height={16} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton variant="text" width="70%" height={16} />
                      </TableCell>
                      {spedFiscalId && (
                        <TableCell align="center">
                          <Skeleton variant="circular" width={24} height={24} />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={spedFiscalId ? 7 : 6} align="center">
                      Nenhum dado encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((item, index) => (
                    <TableRow
                      key={`${item.cod_item}-${index}`}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5'
                      }}
                    >
                      <TableCell align="center" sx={{ width: '50px', fontWeight: 500, color: theme.palette.text.secondary }}>
                        {index + 1}
                      </TableCell>
                      <TableCell>{item.cod_item}</TableCell>
                      <TableCell>{item.descr_compl}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={400} color={theme.palette.success.main}>
                          {formatCurrency(item.credito_icms)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={400} color={theme.palette.error.main}>
                          {formatCurrency(item.debito_icms)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight={400}
                          color={
                            (item.saldo_icms ?? 0) > 0
                              ? theme.palette.error.main
                              : theme.palette.success.main
                          }
                        >
                          {formatCurrency(item.saldo_icms)}
                        </Typography>
                      </TableCell>
                      {spedFiscalId && (
                        <TableCell align="center">
                          <Tooltip title="Ver detalhes do produto">
                            <IconButton
                              size="small"
                              onClick={() => handleProductClick(item)}
                              sx={{ color: theme.palette.primary.main }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>

                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      
      <ProductDrillDownModal
        open={isModalOpen}
        onClose={closeModal}
        data={drillDownData}
        loading={drillDownLoading}
        productName={selectedProductName}
      />
    </>
  );
};

export default TableIcmsProdutos;
